// Signals admin data layer (Team & Admin §4 — /hub/admin/signals).
//
// "Now is driven entirely by rows. A seventh thing that needs a person is an
// INSERT. Reordering the page is an UPDATE to rank." This screen toggles
// `is_active` (on/off) and edits `rank` (ordering) on `ops_signals`. Creating a
// signal is deliberately impossible from the UI — a signal is defined by its
// `detect_sql`, which is engineering's to write — so add/rename/delete is not
// exposed here.
//
// Modelled on api/hub/team.js: throw-on-error, unwrap the `{error:true, message}`
// gate envelope, label errors by RPC name, one jittered retry on a transport
// (not database) failure for READS only. Writes are never retried — a transport
// failure surfaces a real error and the admin re-attempts consciously.
//
// VERIFIED (2026-09-14 — Team & Admin backend answers):
//   ops_signals (staff-readable config table) — signal_key · label · category ·
//     rank · severity · is_active · last_error · last_error_at (and more).
//   fn_signal_set(p_signal_key text!, p_is_active boolean?, p_rank integer?)
//     // either field alone, or both; passing NEITHER raises "nothing to change".
//     // Requires admin.manage (the wall is the database — the UI gate is a
//     // convenience, 00_CONTRACT.md §6).
//
// HUB_STUB is a local-dev-only fixtures override (REACT_APP_HUB_STUB=1).

import { supabase } from "../../lib/supabase";

const HUB_STUB =
  typeof process !== "undefined" &&
  !!process.env &&
  process.env.REACT_APP_HUB_STUB === "1";

// A refused HTTP/2 stream is a transport failure, not a database one — retry once.
// A Postgres error / RLS refusal / bad parameter carries a `code` and fails on the
// first attempt, where it belongs. (Mirrors api/hub/now.js.)
const TRANSPORT_FAILURE =
  /failed to fetch|networkerror|load failed|err_http2|connection closed/i;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const isTransportFailure = (err) =>
  !err?.code && TRANSPORT_FAILURE.test(err?.message || "");

// Shared RPC call. Writes pass `retry: false` — only reads retry on a transport
// failure. The gate envelope ({error:true, message}) is unwrapped and thrown as
// the raw message so the page can surface it verbatim (knowledge-base §5).
async function signalsRpc(name, params, label, { retry = true } = {}) {
  if (HUB_STUB) return signalsStub(name, params);
  const attempt = async () => {
    try {
      return await supabase.rpc(name, params);
    } catch (e) {
      return { data: null, error: e };
    }
  };
  let result = await attempt();
  if (retry && isTransportFailure(result.error)) {
    await sleep(200 + Math.floor(Math.random() * 250));
    result = await attempt();
  }
  const { data, error } = result;
  if (error) {
    const detail = error?.message || "Supabase RPC failed";
    throw new Error(label ? `${label}: ${detail}` : detail);
  }
  if (data && typeof data === "object" && data.error === true) {
    throw new Error(data.message || `${name} returned an error`);
  }
  return data;
}

// The columns the page needs. `last_error` / `last_error_at` are read so the page
// can show a failing detector prominently — a signal that silently stops working
// is the thing this screen exists to catch (spec §4 hard rule).
const SIGNAL_COLUMNS =
  "signal_key,label,category,rank,severity,is_active,last_error,last_error_at";

// Rank ascending, nulls last. The page is "ordered by rank" (spec §4); the table
// itself is not guaranteed ordered, so sort client-side for a stable render.
const byRank = (a, b) => {
  const ra = a?.rank;
  const rb = b?.rank;
  if (ra == null && rb == null) return 0;
  if (ra == null) return 1;
  if (rb == null) return -1;
  return ra - rb;
};

// ── Read: the signal registry ─────────────────────────────────────────────────
// ops_signals is a staff-readable config table. A client-side dashboard user
// signed into the same project reads zero rows from it — that is deliberate, not
// a bug (the read policy gates on ops_team_members membership). An empty array
// means "there are no signals"; a gate refusal throws (the page reads it as
// <NotPermitted>). Never null.
export async function getSignals() {
  if (HUB_STUB) return STUB_SIGNALS.slice().sort(byRank);
  const { data, error } = await supabase
    .from("ops_signals")
    .select(SIGNAL_COLUMNS);
  if (error) throw new Error(`signals: ${error.message}`);
  return (Array.isArray(data) ? data : []).slice().sort(byRank);
}

// ── Write: toggle is_active and/or set rank ───────────────────────────────────
// p_is_active / p_rank are EACH optional. Omitting a key (not sending undefined)
// means "leave that field alone" — PostgREST reads a missing arg as a missing
// function, so we never add undefined keys to the params object. Passing NEITHER
// raises "nothing to change"; the page surfaces that sentence verbatim rather than
// pre-empting it with a client-side diff (the function is the source of truth).
// Never retried — a write is a deliberate action.
export async function setSignal({ signalKey, isActive, rank } = {}) {
  const params = { p_signal_key: signalKey };
  if (isActive !== undefined && isActive !== null) {
    params.p_is_active = isActive;
  }
  if (rank !== undefined && rank !== null) {
    params.p_rank = rank;
  }
  return signalsRpc("fn_signal_set", params, "signal set", { retry: false });
}

// ── Stub (local dev only — HUB_STUB=1) ────────────────────────────────────────
const STUB_SIGNALS = [
  { signal_key: "overdue_task", label: "Overdue task", category: "Work", rank: 1, severity: "high", is_active: true, last_error: null, last_error_at: null },
  { signal_key: "broken_promise", label: "Broken promise", category: "Work", rank: 2, severity: "high", is_active: true, last_error: null, last_error_at: null },
  { signal_key: "raised_hand", label: "Raised hand", category: "Work", rank: 3, severity: "medium", is_active: true, last_error: "detector timed out — skipped", last_error_at: "2026-09-14T03:12:00Z" },
  { signal_key: "stalled_thread", label: "Stalled thread", category: "Conversations", rank: 4, severity: "medium", is_active: false, last_error: null, last_error_at: null },
];

function signalsStub(name) {
  if (name === "fn_signal_set") return { ok: true };
  return null;
}

